import { Layout, MaterialLayout, PartTypeSchema, PricingModeSchema } from "../types";
import { adaptComputedCost } from "./adaptComputedCost";
import { getMaterialWeight } from "./getMaterialWeight";
import { getSheetMetalQuantity } from "./getSheetMetalQuantity";
import { ComputeMaterialPriceParams } from "./newComputeMaterialPrice";

type ComputeMaterialPriceReturn = {
  partMaterialPrice: number;
  partMaterialCost: number;
  partMaterialMarginPrice: number;
};

type ComputeMaterialPriceLayoutParam = Layout & {
  materialLayout: MaterialLayout | null;
};

const computeVolumetricMaterialPrice = (
  layout: ComputeMaterialPriceLayoutParam,
  margin: number,
): ComputeMaterialPriceReturn => {
  const materialLayout = layout.materialLayout;
  const density = materialLayout?.density ?? 0; // in g/cm³
  const volume = materialLayout?.volume ?? 0; // in mm3
  const volumetricPrice = materialLayout?.volumetricPrice ?? 0; // in cents/kg

  const weightKg = getMaterialWeight(volume, density);
  const computedPrice = volumetricPrice * weightKg;
  const computedMarginPrice = (computedPrice * margin || 0) / 100;

  const {
    cost: partMaterialCost,
    marginPrice: partMaterialMarginPrice,
    price: partMaterialPrice,
  } = adaptComputedCost({
    cost: computedPrice,
    marginPrice: computedMarginPrice,
  });

  return {
    partMaterialPrice,
    partMaterialCost,
    partMaterialMarginPrice,
  };
};

const computeOptimizedMaterialPrice = (
  optimizedCost: number,
  margin: number,
): ComputeMaterialPriceReturn => {
  const {
    cost: partMaterialCost,
    marginPrice: partMaterialMarginPrice,
    price: partMaterialPrice,
  } = adaptComputedCost({ cost: optimizedCost, marginPrice: (optimizedCost * margin || 0) / 100 });

  return { partMaterialPrice, partMaterialCost, partMaterialMarginPrice };
};

export const legacyComputeMaterialPrice = ({
  quoteRequestLine,
  optimizedMaterialCosts,
}: ComputeMaterialPriceParams): ComputeMaterialPriceReturn => {
  const { materialMarginRatio, quantity, layout } = quoteRequestLine;

  const materialMargin = materialMarginRatio ?? 0;

  const optimizedCost = optimizedMaterialCosts.get(quoteRequestLine.id);
  if (optimizedCost !== undefined) {
    return computeOptimizedMaterialPrice(optimizedCost, materialMargin);
  }

  if (layout && layout.materialLayout?.pricingMode === PricingModeSchema.enum.VOLUMETRIC) {
    return computeVolumetricMaterialPrice(layout, materialMargin);
  }

  if (!quantity || !layout || layout.useNesting === null) {
    return {
      partMaterialPrice: 0,
      partMaterialCost: 0,
      partMaterialMarginPrice: 0,
    };
  }

  const sheetMetalQuantityNeeded =
    (layout.materialLayout && layout.materialLayout.quantity) ||
    getSheetMetalQuantity({
      partialQuoteRequestLine: {
        quantity,
      },
    });

  const layoutType = layout.materialLayout?.type;
  const shouldIgnoreQuantity =
    !layout.useNesting || layoutType === PartTypeSchema.enum.OTHER || layoutType === null;

  const materialQuantityNeeded = shouldIgnoreQuantity ? 1 : sheetMetalQuantityNeeded;

  const materialQuantityUnused = shouldIgnoreQuantity
    ? 0
    : (materialQuantityNeeded * (layout.scrapRatio || 0)) / 100;

  const materialQuantityUsed = materialQuantityNeeded - materialQuantityUnused;

  const unitPrice = layout.materialLayout?.price ?? 0;

  const logisticsCosts =
    (unitPrice * sheetMetalQuantityNeeded * layout.materialsLogisticsCostsRatio) / (100 * quantity);

  const maxPartsPerLayout = Math.max(layout.materialLayout?.maxPartsPerLayout ?? 0, 1);

  if (layout.hasAutoScrapRatio) {
    const partMaterialCostWithoutLogistics = shouldIgnoreQuantity
      ? unitPrice
      : unitPrice / maxPartsPerLayout;

    const computedPartMaterialCost = partMaterialCostWithoutLogistics + logisticsCosts;
    const computedPartMaterialMarginPrice = (computedPartMaterialCost * materialMargin || 0) / 100;

    const {
      cost: partMaterialCost,
      marginPrice: partMaterialMarginPrice,
      price: partMaterialPrice,
    } = adaptComputedCost({
      cost: computedPartMaterialCost,
      marginPrice: computedPartMaterialMarginPrice,
    });

    return {
      partMaterialPrice,
      partMaterialCost,
      partMaterialMarginPrice,
    };
  }

  const materialQuantityBilled =
    materialQuantityUsed + (materialQuantityUnused * (layout.scrapMaterialBillingRate || 0)) / 100;

  const partMaterialCostWithoutLogistics =
    (materialQuantityBilled * unitPrice) / (shouldIgnoreQuantity ? 1 : quantity);
  const computedPartMaterialCost = partMaterialCostWithoutLogistics + logisticsCosts;
  const computedPartMaterialMarginPrice = (computedPartMaterialCost * materialMargin || 0) / 100;

  const {
    cost: partMaterialCost,
    marginPrice: partMaterialMarginPrice,
    price: partMaterialPrice,
  } = adaptComputedCost({
    cost: computedPartMaterialCost,
    marginPrice: computedPartMaterialMarginPrice,
  });

  return {
    partMaterialPrice,
    partMaterialCost,
    partMaterialMarginPrice,
  };
};
